import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Search, Mail, Star, Briefcase, Calendar, Plus, DollarSign, Globe, Clock, CheckCircle2, AlertCircle, Camera, Ban, Loader2, Bell, BellOff } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TimezoneBadge, WorkingHoursIndicator } from '../components/TimezoneDisplay';
import BandwidthBadge from '../components/BandwidthBadge';
import ImageCropper from '../components/ImageCropper';
import { teamAPI, bandwidthAPI, profileAPI } from '../services/api';
import { toast } from 'sonner';

const Team = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [myVAs, setMyVAs] = useState([]);
  const [vaBandwidth, setVaBandwidth] = useState({});
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isClient = user.role === 'client';
  const isAdmin = user.role === 'admin';
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropMemberId, setCropMemberId] = useState(null);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'Virtual Assistant',
    skills: '',
    rating: 4.5,
    tasks_completed: 0,
    hours_worked: 0,
    status: 'active',
    hourly_rate: 25,
    availability: 'Full-time',
    avatar: ''
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getAll();
      setTeamMembers(response.data);
      
      // For clients, also fetch their assigned VAs with task stats
      if (isClient) {
        try {
          const myVAsResponse = await teamAPI.getMyVAs();
          setMyVAs(myVAsResponse.data.vas || []);
        } catch (e) {
          console.log('getMyVAs not available, using team data');
        }
      }
      
      // Fetch bandwidth for all VAs (admin and client view)
      try {
        const bandwidthResponse = await bandwidthAPI.getAllBandwidth();
        const bandwidthMap = {};
        (bandwidthResponse.data.bandwidth || []).forEach(b => {
          bandwidthMap[b.va_id] = b;
        });
        setVaBandwidth(bandwidthMap);
      } catch (e) {
        console.log('Bandwidth data not available');
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    try {
      // Get current user
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Convert skills from comma-separated string to array
      const skillsArray = newMember.skills.split(',').map(s => s.trim()).filter(s => s);
      
      // Generate avatar URL
      const avatarUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${newMember.name}`;
      
      const memberData = {
        name: newMember.name,
        email: newMember.email,
        role: newMember.role,
        skills: skillsArray,
        rating: newMember.rating,
        tasks_completed: newMember.tasks_completed,
        hours_worked: newMember.hours_worked,
        status: newMember.status,
        hourly_rate: newMember.hourly_rate,
        availability: newMember.availability,
        avatar: avatarUrl,
        client_id: user._id || user.id
      };
      
      const response = await teamAPI.create(memberData);
      setTeamMembers([response.data, ...teamMembers]);
      setIsAddDialogOpen(false);
      setNewMember({
        name: '',
        email: '',
        role: 'Virtual Assistant',
        skills: '',
        rating: 4.5,
        tasks_completed: 0,
        hours_worked: 0,
        status: 'active',
        hourly_rate: 25,
        availability: 'Full-time',
        avatar: ''
      });
      toast.success('Team member added successfully!');
      fetchTeamMembers(); // Refresh the list
    } catch (error) {
      console.error('Failed to add team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleTeamPhotoSelect = (memberId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setCropMemberId(memberId);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleCropComplete = async (croppedFile) => {
    if (!cropMemberId) return;
    try {
      const res = await profileAPI.adminUploadPhoto(cropMemberId, croppedFile);
      setTeamMembers(prev => prev.map(m =>
        (m.id || m._id) === cropMemberId ? { ...m, avatar: res.data.photo_url, photo_url: res.data.photo_url } : m
      ));
      toast.success('Photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const [togglingBookedId, setTogglingBookedId] = useState(null);
  const [notifySubscriptions, setNotifySubscriptions] = useState({});
  const [togglingNotifyId, setTogglingNotifyId] = useState(null);

  // Load notify-me subscriptions for fully booked VAs (client view)
  useEffect(() => {
    if (!isClient) return;
    const loadNotifySubs = async () => {
      const subs = {};
      for (const [vaId, bw] of Object.entries(vaBandwidth)) {
        if (bw.daily_hours > 0 && (bw.hours_available_for_booking || 0) <= 0) {
          try {
            const res = await bandwidthAPI.checkNotifyMe(vaId);
            subs[vaId] = res.data.subscribed;
          } catch { /* ignore */ }
        }
      }
      setNotifySubscriptions(subs);
    };
    if (Object.keys(vaBandwidth).length > 0) loadNotifySubs();
  }, [isClient, vaBandwidth]);

  const handleToggleFullyBooked = async (memberId) => {
    const bw = vaBandwidth[memberId];
    const isCurrentlyBooked = bw && bw.daily_hours > 0 && (bw.hours_available_for_booking || 0) <= 0;
    setTogglingBookedId(memberId);
    try {
      await bandwidthAPI.setFullyBooked(memberId, !isCurrentlyBooked);
      toast.success(isCurrentlyBooked ? 'VA is now available for booking' : 'VA marked as Fully Booked');
      // Re-fetch bandwidth data
      const bandwidthResponse = await bandwidthAPI.getAllBandwidth();
      const bandwidthMap = {};
      (bandwidthResponse.data.bandwidth || []).forEach(b => {
        bandwidthMap[b.va_id] = b;
      });
      setVaBandwidth(bandwidthMap);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking status');
    } finally {
      setTogglingBookedId(null);
    }
  };

  const handleToggleNotifyMe = async (memberId, memberName) => {
    setTogglingNotifyId(memberId);
    try {
      const isSubscribed = notifySubscriptions[memberId];
      if (isSubscribed) {
        await bandwidthAPI.cancelNotifyMe(memberId);
        setNotifySubscriptions(prev => ({ ...prev, [memberId]: false }));
        toast.success(`Notification cancelled for ${memberName}`);
      } else {
        await bandwidthAPI.notifyMe(memberId);
        setNotifySubscriptions(prev => ({ ...prev, [memberId]: true }));
        toast.success(`You'll be notified when ${memberName} becomes available`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notification');
    } finally {
      setTogglingNotifyId(null);
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Use myVAs if available (for clients), otherwise use filtered team members
  const displayMembers = isClient && myVAs.length > 0 ? myVAs.filter(va =>
    va.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) : filteredMembers;

  return (
    <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {isClient ? 'My Virtual Assistants' : isAdmin ? 'Team Directory' : 'My Clients'}
            </h1>
            <p className="text-[#4B5563] mt-1 text-sm">
              {isClient ? 'VAs assigned to work with you' : isAdmin ? 'Manage your virtual assistants' : 'Clients assigned to work with you'}
            </p>
          </div>
          {isAdmin && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#5FD068] hover:bg-[#4ec057] text-white w-full sm:w-auto" data-testid="add-team-member-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Team Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="Enter name"
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      data-testid="team-member-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      data-testid="team-member-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="skills">Skills (comma-separated)</Label>
                  <Input
                    id="skills"
                    placeholder="e.g. Marketing, Content Writing, Social Media"
                    value={newMember.skills}
                    onChange={(e) => setNewMember({ ...newMember, skills: e.target.value })}
                    data-testid="team-member-skills-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Hourly rate hidden - can be enabled later by uncommenting
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      placeholder="25"
                      value={newMember.hourly_rate}
                      onChange={(e) => setNewMember({ ...newMember, hourly_rate: parseFloat(e.target.value) })}
                    />
                  </div>
                  */}
                  <div className="space-y-2">
                    <Label htmlFor="availability">Availability</Label>
                    <Select value={newMember.availability} onValueChange={(value) => setNewMember({ ...newMember, availability: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Full-time">Full-time</SelectItem>
                        <SelectItem value="Part-time">Part-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={handleAddMember}
                  className="w-full bg-[#5FD068] hover:bg-[#4ec057] text-white"
                  disabled={!newMember.name || !newMember.email || !newMember.skills}
                  data-testid="submit-team-member-btn"
                >
                  Add Team Member
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Client's Task Summary with VAs */}
        {isClient && myVAs.length > 0 && (
          <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-none">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div>
                  <h3 className="font-medium text-[#0A0D0A]">Your VA Team Summary</h3>
                  <p className="text-sm text-[#4B5563] mt-1">
                    {myVAs.length} VA{myVAs.length !== 1 ? 's' : ''} assigned to you
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {myVAs.reduce((acc, va) => acc + (va.completed_tasks || 0), 0)}
                    </p>
                    <p className="text-xs text-[#4B5563]">Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {myVAs.reduce((acc, va) => acc + (va.pending_tasks || 0), 0)}
                    </p>
                    <p className="text-xs text-[#4B5563]">Pending</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {myVAs.reduce((acc, va) => acc + (va.total_tasks || 0), 0)}
                    </p>
                    <p className="text-xs text-[#4B5563]">Total Tasks</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF] w-5 h-5" />
              <Input
                placeholder={isClient ? "Search VAs..." : isAdmin ? "Search team members..." : "Search clients..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="team-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64" data-testid="team-loader">
            <Loader2 className="w-8 h-8 text-[#5FD068] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayMembers.map((member) => (
              <Card key={member.id || member._id} className="border border-[#E5E7EB] hover:border-[#5FD068]/30 hover:-translate-y-0.5 transition-all duration-300 flex flex-col" data-testid={`va-card-${member.id || member._id}`}>
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="flex flex-col items-center text-center flex-1">
                    <div className="relative group mb-4">
                      <img
                        src={member.photo_url || member.avatar}
                        alt={member.name}
                        className="w-24 h-24 rounded-full border-4 border-[#E5E7EB] object-cover"
                      />
                      {isAdmin && (
                        <label
                          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          data-testid={`upload-photo-${member.id || member._id}`}
                        >
                          <div className="bg-black/50 rounded-full w-24 h-24 flex items-center justify-center">
                            <Camera className="w-5 h-5 text-white" />
                          </div>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => handleTeamPhotoSelect(member.id || member._id, e)}
                          />
                        </label>
                      )}
                    </div>
                    <h3 className="text-lg font-medium text-[#0A0D0A] mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{member.name}</h3>
                    <p className="text-sm text-[#4B5563] mb-3">{member.role || 'Virtual Assistant'}</p>
                    
                    <div className="flex items-center gap-1 mb-4">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium text-[#0A0D0A]">{member.rating || 5}</span>
                    </div>

                    <div className="flex gap-2 mb-3 flex-wrap justify-center">
                      <Badge
                        variant="outline"
                        className={member.status === 'active' ? 'bg-[#E5F7E7] text-[#4BB854] border-[#5FD068]/20' : 'bg-orange-50 text-orange-700 border-orange-200'}
                      >
                        {member.status === 'active' ? 'Active' : 'On Leave'}
                      </Badge>
                      {member.hire_status && member.hire_status !== 'active' && (
                        <Badge
                          variant="outline"
                          className={member.hire_status === 'approved' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}
                        >
                          {member.hire_status === 'approved' ? 'Hired' : member.hire_status}
                        </Badge>
                      )}
                      {/* Fully Booked badge — shown when all bandwidth is allocated */}
                      {vaBandwidth[member.id || member._id] &&
                        vaBandwidth[member.id || member._id].daily_hours > 0 &&
                        (vaBandwidth[member.id || member._id].hours_available_for_booking || 0) <= 0 && (
                        <Badge
                          variant="outline"
                          className="bg-amber-50 text-amber-800 border-amber-300 font-semibold"
                          data-testid={`fully-booked-badge-${member.id || member._id}`}
                        >
                          Fully Booked
                        </Badge>
                      )}
                    </div>

                    {/* Timezone Display */}
                    <div className="flex items-center gap-2 mt-1">
                      <TimezoneBadge timezone={member.working_timezone || 'IST'} />
                      <WorkingHoursIndicator timezone={member.working_timezone || 'IST'} />
                    </div>

                    {/* Bandwidth Display */}
                    {vaBandwidth[member.id || member._id] && (
                      <div className="mt-3 w-full">
                        <BandwidthBadge
                          hoursAvailable={vaBandwidth[member.id || member._id].hours_available_today}
                          dailyHours={vaBandwidth[member.id || member._id].daily_hours}
                          hoursUsedToday={vaBandwidth[member.id || member._id].hours_used_today}
                          planName={vaBandwidth[member.id || member._id].plan_name}
                          size="compact"
                        />
                      </div>
                    )}

                    <div className="w-full mt-6 pt-6 border-t border-[#E5E7EB] space-y-3">
                      {/* Task stats for clients */}
                      {isClient && (member.total_tasks !== undefined || member.pending_tasks !== undefined) && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-[#5FD068]" />
                              Completed
                            </span>
                            <span className="font-medium text-[#5FD068]">{member.completed_tasks || 0}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-orange-500" />
                              Pending
                            </span>
                            <span className="font-medium text-orange-600">{member.pending_tasks || 0}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              Total Tasks
                            </span>
                            <span className="font-medium text-[#0A0D0A]">{member.total_tasks || 0}</span>
                          </div>
                        </>
                      )}
                      
                      {/* Default stats for non-clients */}
                      {(!isClient || (member.total_tasks === undefined && member.pending_tasks === undefined)) && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Working Hours
                            </span>
                            <span className="font-medium text-[#0A0D0A]">9 AM - 6 PM</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              Tasks
                            </span>
                            <span className="font-medium text-[#0A0D0A]">{member.tasks_completed || member.tasksCompleted || 0}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4B5563] flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Hours
                            </span>
                            <span className="font-medium text-[#0A0D0A]">{member.hours_worked || member.hoursWorked || 0}</span>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="w-full mt-4">
                      <p className="text-xs text-[#4B5563] mb-2 font-medium">Skills</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {(member.skills || []).slice(0, 3).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {(!member.skills || member.skills.length === 0) && (
                          <span className="text-xs text-[#9CA3AF]">No skills listed</span>
                        )}
                      </div>
                    </div>

                    <div className="w-full mt-auto pt-4 space-y-2">
                      {/* Admin: Toggle Fully Booked */}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={`w-full text-xs ${
                            vaBandwidth[member.id || member._id] &&
                            vaBandwidth[member.id || member._id].daily_hours > 0 &&
                            (vaBandwidth[member.id || member._id].hours_available_for_booking || 0) <= 0
                              ? 'border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                          }`}
                          disabled={togglingBookedId === (member.id || member._id)}
                          onClick={() => handleToggleFullyBooked(member.id || member._id)}
                          data-testid={`toggle-fully-booked-${member.id || member._id}`}
                        >
                          {togglingBookedId === (member.id || member._id) ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Ban className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {vaBandwidth[member.id || member._id] &&
                           vaBandwidth[member.id || member._id].daily_hours > 0 &&
                           (vaBandwidth[member.id || member._id].hours_available_for_booking || 0) <= 0
                            ? 'Unmark Fully Booked'
                            : 'Mark Fully Booked'}
                        </Button>
                      )}
                      {/* Client: Notify Me when VA is fully booked */}
                      {isClient &&
                        vaBandwidth[member.id || member._id] &&
                        vaBandwidth[member.id || member._id].daily_hours > 0 &&
                        (vaBandwidth[member.id || member._id].hours_available_for_booking || 0) <= 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={`w-full text-xs ${
                            notifySubscriptions[member.id || member._id]
                              ? 'border-amber-300 text-amber-700 hover:bg-amber-50'
                              : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                          }`}
                          disabled={togglingNotifyId === (member.id || member._id)}
                          onClick={() => handleToggleNotifyMe(member.id || member._id, member.name)}
                          data-testid={`notify-me-team-${member.id || member._id}`}
                        >
                          {togglingNotifyId === (member.id || member._id) ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : notifySubscriptions[member.id || member._id] ? (
                            <BellOff className="w-3.5 h-3.5 mr-1.5" />
                          ) : (
                            <Bell className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {notifySubscriptions[member.id || member._id] ? 'Cancel Notification' : 'Notify Me When Available'}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="w-full border-[#E5E7EB] hover:border-[#5FD068] hover:text-[#4BB854]" data-testid={`message-btn-${member.id || member._id}`}>
                        <Mail className="w-4 h-4 mr-1" />
                        Message
                      </Button>
                      <Button 
                        className="bg-[#5FD068] hover:bg-[#4BB854] text-white w-full" 
                        size="sm"
                        onClick={() => navigate(`/va/${member.id || member._id}`)}
                        data-testid={`view-profile-btn-${member.id || member._id}`}
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && displayMembers.length === 0 && (
          <Card className="border border-[#E5E7EB]">
            <CardContent className="p-12 text-center">
              <p className="text-[#4B5563]">
                {isClient ? 'No VAs assigned to you yet. Hire a VA to get started!' : isAdmin ? 'No team members found.' : 'No clients assigned to you yet. Clients will appear here once assigned.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Image Cropper Dialog */}
        <ImageCropper
          open={!!cropImage}
          onClose={() => { setCropImage(null); setCropMemberId(null); }}
          imageSrc={cropImage}
          onCropComplete={handleCropComplete}
        />
      </div>  );
};

export default Team;
